/** Shared chart dataset + PDF payload builders for Privacy Reports. */
import { hasReportLicense, isChartMetricLicensed } from "./reportLicenses";


export const REPORT_CHART_PREFS_KEY = "onedpdp_report_chart_prefs";

export const DEFAULT_CHART_PREFS = {
  visible: true,
  panelOpen: false,
  primaryType: "bar",
  secondaryType: "pie",
  palette: "ocean",
  primaryMetric: "auto",
  secondaryMetric: "auto",
  showLegend: true,
  showGrid: true,
  showLabels: true,
  animate: true,
};

function pct(value, total) {
  const t = Number(total) || 0;
  const v = Number(value) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((v / t) * 100));
}

export function loadChartPrefs() {
  try {
    const raw = localStorage.getItem(REPORT_CHART_PREFS_KEY);
    if (!raw) return { ...DEFAULT_CHART_PREFS };
    return { ...DEFAULT_CHART_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_CHART_PREFS };
  }
}

export function resolveChartMetric(metricId, dataSource) {
  if (metricId !== "auto") return metricId;
  if (dataSource === "consent") return "consent";
  if (dataSource === "cookie") return "cookie";
  if (dataSource === "dsar") return "dsar";
  if (dataSource === "tprm") return "tprm";
  if (dataSource === "dspm") return "dspm";
  if (dataSource === "breach") return "breach";
  if (dataSource === "consent_variance") return "consent_variance";
  if (dataSource === "policy_store") return "policy_store";
  if (dataSource === "roadmap") return "roadmap";
  if (dataSource === "gap" || dataSource === "pia" || dataSource === "ropa") return "compliance";
  return "modules";
}

export function buildChartDataset(metric, data, showModule) {
  switch (metric) {
    case "modules": {
      const rows = [];
      if (showModule("consent")) {
        rows.push({ name: "Consent", value: pct(data.consentAccepted, data.consentTotal) });
      }
      if (showModule("cookie")) {
        rows.push({ name: "Cookie", value: pct(data.cookieAccepted, data.cookieTotal) });
      }
      if (showModule("dsar")) {
        rows.push({ name: "DSAR", value: pct(data.dsarProcessed, data.dsarTotal) });
      }
      if (showModule("gap")) {
        rows.push({ name: "Gap", value: data.gapScore ?? 0 });
      }
      if (showModule("tprm")) {
        rows.push({ name: "TPRM", value: data.tprmAvgScore ?? 0 });
      }
      if (showModule("ropa")) {
        rows.push({ name: "ROPA", value: data.ropaProgressPct ?? 0 });
      }
      if (showModule("pia")) {
        rows.push({
          name: "PIA",
          value: data.piaSensitive > 0 ? pct(data.piaEncrypted, data.piaSensitive) : 0,
        });
      }
      if (showModule("breach")) {
        rows.push({
          name: "Breach",
          value: data.breachTotal > 0 ? pct(data.breachResolved, data.breachTotal) : 0,
        });
      }
      if (showModule("consent_variance")) {
        rows.push({
          name: "Variance",
          value: data.varianceTotal > 0 ? pct(data.varianceAligned, data.varianceTotal) : 0,
        });
      }
      if (showModule("roadmap")) {
        rows.push({ name: "Roadmap", value: data.roadmapOverallPct ?? 0 });
      }
      return {
        title: "Module performance",
        subtitle: "Completion and acceptance rates (%)",
        data: rows,
        unit: "%",
      };
    }
    case "compliance": {
      const allow = (id) => !showModule || showModule(id);
      const rows = [];
      if (allow("gap")) {
        rows.push({ name: "Compliance", value: data.complianceScore ?? 0 });
        rows.push({ name: "Open gaps", value: data.gapGaps ?? 0 });
      }
      if (allow("tprm")) {
        rows.push({ name: "High-risk vendors", value: data.tprmHighRisk ?? 0 });
      }
      if (allow("pia")) {
        rows.push({ name: "Unencrypted PII", value: data.piaUnencrypted ?? 0 });
      }
      if (allow("dsar")) {
        rows.push({ name: "Pending DSARs", value: data.dsarPending ?? 0 });
      }
      if (allow("breach")) {
        rows.push({ name: "Open breaches", value: data.breachActive ?? 0 });
      }
      if (allow("consent_variance")) {
        rows.push({ name: "Consent variance", value: data.varianceWithVariance ?? 0 });
      }
      return {
        title: "Compliance and risk snapshot",
        subtitle: "Key indicators",
        data: rows,
        unit: "",
      };
    }
    case "consent":
      return {
        title: "Consent events",
        subtitle: "Distribution by outcome",
        data: [
          { name: "Accepted", value: data.consentAccepted ?? 0 },
          { name: "Revoked", value: data.consentRevoked ?? 0 },
          { name: "Declined", value: data.consentDeclined ?? 0 },
        ],
        unit: "",
      };
    case "cookie":
      return {
        title: "Cookie events",
        subtitle: "Distribution by outcome",
        data: [
          { name: "Accepted", value: data.cookieAccepted ?? 0 },
          { name: "Rejected", value: data.cookieRejected ?? 0 },
          { name: "Pending", value: data.cookiePending ?? 0 },
        ],
        unit: "",
      };
    case "dsar":
      return {
        title: "DSAR requests",
        subtitle: "Processing status",
        data: [
          { name: "Processed", value: data.dsarProcessed ?? 0 },
          { name: "Pending", value: data.dsarPending ?? 0 },
          { name: "Rejected", value: data.dsarRejected ?? 0 },
        ],
        unit: "",
      };
    case "tprm":
      return {
        title: "Third-party risk",
        subtitle: "Assessment status",
        data: [
          { name: "Submitted", value: data.tprmSubmitted ?? 0 },
          { name: "Pending", value: data.tprmPending ?? 0 },
          { name: "High risk", value: data.tprmHighRisk ?? 0 },
        ],
        unit: "",
      };
    case "dspm": {
      const top = (data.dspmBreakdown || []).slice(0, 6);
      if (top.length === 0) {
        return {
          title: "DSPM inventory",
          subtitle: "No inventory data yet",
          data: [],
          unit: "",
        };
      }
      return {
        title: "DSPM data types",
        subtitle: "Top categories by record count",
        data: top.map((r) => ({ name: r.label, value: r.count })),
        unit: "",
      };
    }
    case "breach":
      return {
        title: "Breach incidents",
        subtitle: "Resolution status",
        data: [
          { name: "Active", value: data.breachActive ?? 0 },
          { name: "Pending", value: data.breachPending ?? 0 },
          { name: "Resolved", value: data.breachResolved ?? 0 },
        ],
        unit: "",
      };
    case "consent_variance":
      return {
        title: "Consent variance",
        subtitle: "Reconsent status",
        data: [
          { name: "Aligned", value: data.varianceAligned ?? 0 },
          { name: "Required", value: data.varianceReconsentRequired ?? 0 },
          { name: "Pending", value: data.varianceReconsentPending ?? 0 },
          { name: "Completed", value: data.varianceReconsentCompleted ?? 0 },
        ],
        unit: "",
      };
    case "policy_store":
      return {
        title: "Policy store",
        subtitle: "Document sources",
        data: [
          { name: "Uploaded", value: data.policyUploaded ?? 0 },
          { name: "AI generated", value: data.policyGenerated ?? 0 },
        ],
        unit: "",
      };
    case "roadmap":
      return {
        title: "Compliance roadmap",
        subtitle: "Module completion (%)",
        data: [
          { name: "Gap", value: data.roadmapAssessPct ?? 0 },
          { name: "ROPA", value: data.roadmapRopaPct ?? 0 },
          { name: "PIA", value: data.roadmapPiaPct ?? 0 },
          { name: "TPRM", value: data.roadmapTprmPct ?? 0 },
        ],
        unit: "%",
      };
    default:
      return buildChartDataset("modules", data, showModule);
  }
}

/** Leadership chart for the executive report. Scoped views stay high-level. */
export function buildScopedExecutiveDataset(data, source, showModule) {
  if (!source || source === "all") {
    return buildChartDataset("compliance", data, showModule);
  }
  const acceptedShare = (accepted, total, acceptedLabel) => ({
    title: `${acceptedLabel} snapshot`,
    subtitle: "Accepted compared with every other outcome",
    data: [
      { name: "Accepted", value: accepted ?? 0 },
      { name: "Other", value: Math.max(0, (total ?? 0) - (accepted ?? 0)) },
    ],
    unit: "",
  });
  switch (source) {
    case "consent":
      return acceptedShare(data.consentAccepted, data.consentTotal, "Consent");
    case "cookie":
      return acceptedShare(data.cookieAccepted, data.cookieTotal, "Cookie");
    case "dsar":
      return {
        title: "DSAR snapshot",
        subtitle: "Processed compared with requests still open",
        data: [
          { name: "Processed", value: data.dsarProcessed ?? 0 },
          { name: "Still open", value: Math.max(0, (data.dsarTotal ?? 0) - (data.dsarProcessed ?? 0)) },
        ],
        unit: "",
      };
    case "gap":
      return {
        title: "Gap assessment snapshot",
        subtitle: "Compliance score and open gaps",
        data: [
          { name: "Score", value: data.gapScore ?? data.complianceScore ?? 0 },
          { name: "Open gaps", value: data.gapGaps ?? 0 },
        ],
        unit: "",
      };
    case "pia":
      return {
        title: "PIA snapshot",
        subtitle: "Encrypted compared with unencrypted sensitive fields",
        data: [
          { name: "Encrypted", value: data.piaEncrypted ?? 0 },
          { name: "Unencrypted", value: data.piaUnencrypted ?? 0 },
        ],
        unit: "",
      };
    case "tprm":
      return {
        title: "Vendor risk snapshot",
        subtitle: "High-risk vendors compared with the rest",
        data: [
          { name: "High risk", value: data.tprmHighRisk ?? 0 },
          { name: "Other", value: Math.max(0, (data.tprmTotal ?? 0) - (data.tprmHighRisk ?? 0)) },
        ],
        unit: "",
      };
    case "ropa":
      return {
        title: "ROPA snapshot",
        subtitle: "Completion of the processing register",
        data: [{ name: "Progress", value: data.ropaProgressPct ?? 0 }],
        unit: "%",
      };
    case "dspm":
      return {
        title: "DSPM snapshot",
        subtitle: "Discovered records and categories",
        data: [
          { name: "Records", value: data.dspmTotal ?? 0 },
          { name: "Categories", value: data.dspmBreakdown?.length ?? 0 },
        ],
        unit: "",
      };
    case "breach":
      return {
        title: "Breach snapshot",
        subtitle: "Open incidents compared with resolved ones",
        data: [
          { name: "Active", value: data.breachActive ?? 0 },
          { name: "Resolved", value: data.breachResolved ?? 0 },
        ],
        unit: "",
      };
    case "consent_variance":
      return {
        title: "Consent variance snapshot",
        subtitle: "Aligned records compared with records that differ",
        data: [
          { name: "Aligned", value: data.varianceAligned ?? 0 },
          { name: "With variance", value: data.varianceWithVariance ?? 0 },
        ],
        unit: "",
      };
    case "policy_store":
      return {
        title: "Policy store snapshot",
        subtitle: "Uploaded documents compared with AI-generated ones",
        data: [
          { name: "Uploaded", value: data.policyUploaded ?? 0 },
          { name: "AI generated", value: data.policyGenerated ?? 0 },
        ],
        unit: "",
      };
    case "roadmap":
      return buildChartDataset("roadmap", data, showModule);
    default:
      return buildChartDataset("modules", data, showModule);
  }
}

function datasetHasValues(dataset) {
  return (dataset?.data || []).some((d) => Number(d.value) > 0);
}

/** Map UI chart type to backend PDF chart payload. */
export function datasetToPdfChart(uiType, dataset) {
  const labels = (dataset.data || []).map((d) => d.name);
  const values = (dataset.data || []).map((d) => Number(d.value) || 0);
  const title = dataset.title || "Chart";
  const suffix = dataset.unit || "";

  if (uiType === "pie") {
    return { type: "pie", title, labels, values };
  }

  if (uiType === "line" || uiType === "area") {
    return { type: "line", title, labels, values, suffix };
  }

  const useHorizontal = labels.length > 5 || labels.some((l) => String(l).length > 14);
  const chart = {
    type: useHorizontal ? "bar_horizontal" : "bar_vertical",
    title,
    labels,
    values,
  };
  if (suffix === "%") {
    chart.suffix = "%";
    chart.max_value = 100;
  }
  return chart;
}

function buildRiskSeverityPie(riskItems) {
  if (!riskItems?.length) return null;
  const high = riskItems.filter((r) => r.severity === "High").length;
  const medium = riskItems.filter((r) => r.severity === "Medium").length;
  const low = riskItems.filter((r) => r.severity === "Low").length;
  if (high + medium + low === 0) return null;
  return {
    type: "pie",
    title: "Risk severity distribution",
    labels: ["High", "Medium", "Low"],
    values: [high, medium, low],
  };
}

/** Module-specific pie chart for detailed report sections. */
export function moduleDetailPdfChart(moduleId, data) {
  switch (moduleId) {
    case "consent":
      if (!(data.consentTotal > 0)) return null;
      return {
        type: "pie",
        title: "Consent event distribution",
        labels: ["Accepted", "Revoked", "Declined"],
        values: [data.consentAccepted ?? 0, data.consentRevoked ?? 0, data.consentDeclined ?? 0],
      };
    case "cookie":
      if (!(data.cookieTotal > 0)) return null;
      return {
        type: "pie",
        title: "Cookie event distribution",
        labels: ["Accepted", "Rejected", "Pending"],
        values: [data.cookieAccepted ?? 0, data.cookieRejected ?? 0, data.cookiePending ?? 0],
      };
    case "dsar":
      if (!(data.dsarTotal > 0)) return null;
      return {
        type: "pie",
        title: "DSAR request status",
        labels: ["Processed", "Pending", "Rejected"],
        values: [data.dsarProcessed ?? 0, data.dsarPending ?? 0, data.dsarRejected ?? 0],
      };
    case "tprm":
      if (!(data.tprmTotal > 0)) return null;
      return {
        type: "bar_vertical",
        title: "TPRM assessment status",
        labels: ["Submitted", "Pending", "High risk"],
        values: [data.tprmSubmitted ?? 0, data.tprmPending ?? 0, data.tprmHighRisk ?? 0],
      };
    case "gap":
      if (data.gapScore == null) return null;
      return {
        type: "donut",
        title: "Gap assessment score",
        value: data.gapScore,
        max_value: 100,
        suffix: "%",
        label: data.gapComplianceLabel || "Compliance",
      };
    case "ropa":
      return {
        type: "donut",
        title: "ROPA completion",
        value: data.ropaProgressPct ?? 0,
        max_value: 100,
        suffix: "%",
        label: "Progress",
      };
    case "pia":
      if (!(data.piaSensitive > 0)) return null;
      return {
        type: "pie",
        title: "PIA encryption coverage",
        labels: ["Encrypted", "Unencrypted"],
        values: [data.piaEncrypted ?? 0, data.piaUnencrypted ?? 0],
      };
    case "dspm": {
      const top = (data.dspmBreakdown || []).slice(0, 8);
      if (!top.length) return null;
      return {
        type: "bar_horizontal",
        title: "DSPM records by data type",
        labels: top.map((r) => r.label),
        values: top.map((r) => r.count),
      };
    }
    case "breach":
      if (!(data.breachTotal > 0)) return null;
      return {
        type: "pie",
        title: "Breach incident status",
        labels: ["Active", "Pending", "Resolved"],
        values: [data.breachActive ?? 0, data.breachPending ?? 0, data.breachResolved ?? 0],
      };
    case "consent_variance":
      if (!(data.varianceTotal > 0)) return null;
      return {
        type: "pie",
        title: "Consent variance status",
        labels: ["Aligned", "Reconsent required", "Pending", "Completed"],
        values: [
          data.varianceAligned ?? 0,
          data.varianceReconsentRequired ?? 0,
          data.varianceReconsentPending ?? 0,
          data.varianceReconsentCompleted ?? 0,
        ],
      };
    case "policy_store":
      if (!(data.policyTotal > 0)) return null;
      return {
        type: "pie",
        title: "Policy document sources",
        labels: ["Uploaded", "AI generated"],
        values: [data.policyUploaded ?? 0, data.policyGenerated ?? 0],
      };
    case "roadmap": {
      const entries = [
        ["Gap", data.roadmapAssessPct ?? 0, "gap"],
        ["ROPA", data.roadmapRopaPct ?? 0, "ropa"],
        ["PIA", data.roadmapPiaPct ?? 0, "pia"],
        ["TPRM", data.roadmapTprmPct ?? 0, "tprm"],
      ].filter(([, , id]) => !data.licensedModules || hasReportLicense(data.licensedModules, id));
      if (!entries.length) return null;
      return {
        type: "bar_vertical",
        title: "Compliance roadmap progress",
        labels: entries.map((entry) => entry[0]),
        values: entries.map((entry) => entry[1]),
        suffix: "%",
        max_value: 100,
      };
    }
    default:
      return null;
  }
}

/**
 * Chart payloads for PDF export. Each report type gets a different set so the
 * file matches the on-screen preview for that type and data scope.
 */
export function buildReportPdfCharts({ data, source, showModule, chartPrefs, tab }) {
  const prefs = chartPrefs || loadChartPrefs();

  if (tab === "executive") {
    const charts = [];
    if (
      data.complianceScore != null &&
      (!showModule || showModule("gap"))
    ) {
      charts.push({
        type: "donut",
        title: "Overall compliance score",
        value: data.complianceScore,
        max_value: 100,
        suffix: "%",
        label: data.complianceLabel || "Compliance",
      });
    }
    const snapshot = buildScopedExecutiveDataset(data, source, showModule);
    if (datasetHasValues(snapshot)) {
      charts.push(datasetToPdfChart("bar", snapshot));
    }
    if (data.riskItems?.length) {
      const riskPie = buildRiskSeverityPie(data.riskItems);
      if (riskPie) charts.push(riskPie);
    }
    return charts;
  }

  if (tab === "summary") {
    const comparison = buildChartDataset("modules", data, showModule);
    return datasetHasValues(comparison)
      ? [datasetToPdfChart("bar", comparison)]
      : [];
  }

  const safePrimary = isChartMetricLicensed(prefs.primaryMetric, showModule)
    ? prefs.primaryMetric
    : "auto";
  const safeSecondary = isChartMetricLicensed(prefs.secondaryMetric, showModule)
    ? prefs.secondaryMetric
    : "auto";
  const primaryMetric = resolveChartMetric(safePrimary, source);
  let secondaryMetric = resolveChartMetric(safeSecondary, source);
  if (secondaryMetric === primaryMetric && safeSecondary === "auto") {
    secondaryMetric = primaryMetric === "modules" && (!showModule || showModule("consent"))
      ? "consent"
      : "modules";
  }
  if (!isChartMetricLicensed(secondaryMetric, showModule)) {
    secondaryMetric = "modules";
  }

  const charts = [];
  const primaryDataset = buildChartDataset(primaryMetric, data, showModule);
  const secondaryDataset = buildChartDataset(secondaryMetric, data, showModule);
  if (datasetHasValues(primaryDataset)) {
    charts.push(datasetToPdfChart(prefs.primaryType, primaryDataset));
  }
  if (
    secondaryMetric !== primaryMetric &&
    datasetHasValues(secondaryDataset)
  ) {
    charts.push(datasetToPdfChart(prefs.secondaryType, secondaryDataset));
  }
  return charts;
}
